import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { pickOption, readMap, paintReactive, thenText, onPromoChange, promoState, formatMoney } from '@theme/aromely-pricing';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {Object} Variant
 * @property {number} id
 * @property {string} title
 * @property {number} price
 * @property {number|null} compare_at_price
 * @property {boolean} available
 * @property {string[]} options
 * @property {SellingPlanAllocation[]} [selling_plan_allocations]
 */

/**
 * @typedef {Object} SellingPlanAllocation
 * @property {number} selling_plan_id
 * @property {number} per_delivery_price
 * @property {number|null} compare_at_price
 */

/**
 * @typedef {Object} AromelyPdpRefs
 * @property {HTMLInputElement} variantIdInput
 * @property {HTMLInputElement} sellingPlanInput
 * @property {HTMLElement} priceDisplay
 * @property {HTMLElement} [comparePriceDisplay]
 * @property {HTMLElement} [discountBadge]
 * @property {HTMLButtonElement} submitBtn
 * @property {HTMLElement} submitBtnLabel
 * @property {HTMLFormElement} productForm
 */

/**
 * Custom PDP component handling variant selection, selling plans, and add-to-cart.
 * @extends {Component<AromelyPdpRefs>}
 */
class AromelyPdp extends Component {
  /** @type {Variant[]} */
  #variants = [];

  /** @type {string} */
  #variantsJson = '';

  /** @type {string} */
  #moneyFormat = '';

  /** @type {string} */
  #currency = '';

  /** @type {string} */
  #tAddToCartFor = '';

  /** @type {string} */
  #tSoldOut = '';

  /** @type {AbortController} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const fitQuantity = this.querySelector('[data-fit-pdp-quantity]');
    const requestedQuantity = new URL(window.location.href).searchParams.get('quantity');
    if (fitQuantity && /^[1-4]$/.test(requestedQuantity || '')) {
      const select = fitQuantity.querySelector('select');
      if (!select.dataset.initialized) {
        select.value = requestedQuantity;
        select.dataset.initialized = 'true';
      }
      select.disabled = false;
      fitQuantity.hidden = false;
      select.addEventListener('change', () => {
        const variant = this.#readVariants().find(v => String(v.id) === this.refs.variantIdInput?.value);
        if (variant) this.#updatePriceDisplay(variant);
      }, { signal });
    }

    this.#moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.#currency = this.dataset.currency || 'USD';
    this.#tAddToCartFor = this.dataset.tAddToCartFor || 'Add to cart for __PRICE__';
    this.#tSoldOut = this.dataset.tSoldOut || 'Sold out';

    // Repaint from the price map when the sale starts or ends in the browser, or
    // when the price line is morphed in from the other state.
    onPromoChange(this.#repaint, { signal });
    this.addEventListener('aromely:price-stale', this.#repaint, { signal });
    // Replay the exact selection when the gift runtime mounts after this component.
    document.addEventListener('aromely:promo-ready', this.#repaint, { signal });

    this.#bindOptions();

    // Sync hidden selling_plan input from the initially checked radio
    const checkedPlan = /** @type {HTMLInputElement|null} */ (
      this.querySelector('input[name="selling-plan-option"]:checked')
    );
    if (checkedPlan && this.refs.sellingPlanInput) {
      this.refs.sellingPlanInput.value = checkedPlan.value;
    }

    // Paint the browser's promotion state for the checked plan: the server
    // painted its own clock's state, which a cached page may have outlived.
    const initialVariantId = this.refs.variantIdInput?.value;
    const initialVariant = this.#readVariants().find((v) => v.id.toString() === initialVariantId);
    if (initialVariant) {
      this.#updatePriceDisplay(initialVariant);
    }
  }

  /**
   * Quick-add morphs the next product into this same element without a
   * reconnect: bind any new option radios and repaint from its price map.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#bindOptions();
    this.#repaint();
  }

  /**
   * Variants of the product currently rendered. Re-read from the JSON script
   * because quick-add morphs another product into this same element.
   * @returns {Variant[]}
   */
  #readVariants() {
    const json = this.querySelector('[data-variants-json]')?.textContent || '[]';
    if (json !== this.#variantsJson) {
      try {
        this.#variants = JSON.parse(json);
        this.#variantsJson = json;
      } catch { /* empty */ }
    }
    return this.#variants;
  }

  /**
   * Listen to the option and selling plan radios. Safe to repeat: the same
   * listener is only added once per radio.
   */
  #bindOptions() {
    const signal = this.#abortController?.signal;

    // Variant radio change — each option group has its own name="option-{handle}"
    this.querySelectorAll('input[type="radio"][data-option-index]').forEach((radio) => {
      radio.addEventListener('change', this.#onVariantChange, { signal });
    });

    // Selling plan radio change
    this.querySelectorAll('input[name="selling-plan-option"]').forEach((radio) => {
      radio.addEventListener('change', this.#onSellingPlanChange, { signal });
    });
  }

  /**
   * Repaint the current variant without user input (sale flip, morph): an
   * add-to-cart in flight keeps its button state.
   */
  #repaint = () => {
    const v = this.#readVariants().find((x) => String(x.id) === this.refs.variantIdInput?.value);
    if (v) this.#updatePriceDisplay(v, true);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Handles add-to-cart via fetch POST.
   * @param {Event} event
   */
  /** @type {number|undefined} */
  #successTimeout;

  handleSubmit(event) {
    event.preventDefault();

    const form = this.refs.productForm;
    if (!form) return;

    const btn = this.refs.submitBtn;
    if (!btn || btn.disabled) return;
    if (btn.dataset.state === 'loading') return;

    // Show loading state
    btn.dataset.state = 'loading';
    btn.disabled = true;

    const formData = new FormData(form);

    // Remove selling_plan if empty (one-time purchase)
    if (!formData.get('selling_plan')) {
      formData.delete('selling_plan');
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error('Add to cart error:', response.description);
          btn.dataset.state = '';
          btn.disabled = false;
          return;
        }

        // Show success checkmark
        btn.dataset.state = 'success';

        this.dispatchEvent(
          new CartAddEvent(response, this.id, {
            source: 'aromely-pdp',
            productId: this.dataset.productId,
            variantId: formData.get('id')?.toString(),
          })
        );

        // Reset after delay
        if (this.#successTimeout) clearTimeout(this.#successTimeout);
        this.#successTimeout = setTimeout(() => {
          btn.dataset.state = '';
          btn.disabled = false;
        }, 1500);
      })
      .catch((error) => {
        console.error('Add to cart error:', error);
        btn.dataset.state = '';
        btn.disabled = false;
      });
  }

  /**
   * When a variant option (size) is selected, find the matching variant
   * and update the hidden input + price display.
   */
  #onVariantChange = (e) => {
    // Collect selected option values from each option group
    const selectedOptions = [];
    const optionGroups = this.querySelectorAll('[data-option-group]');

    if (optionGroups.length > 0) {
      // Multi-option: resolve variant from combination of selected values
      for (const group of optionGroups) {
        const checked = /** @type {HTMLInputElement|null} */ (
          group.querySelector('input[type="radio"]:checked')
        );
        if (checked) {
          selectedOptions[parseInt(checked.dataset.optionIndex, 10)] = checked.dataset.optionValue;
        }
      }

      // Find variant matching all selected options
      const variant = this.#readVariants().find((v) =>
        v.options.every((opt, i) => selectedOptions[i] === undefined || opt === selectedOptions[i])
      );
      if (!variant) return;

      this.#applyVariantChange(variant, e?.target);
    } else {
      // Single-option fallback: use the radio value directly as variant ID
      const selected = /** @type {HTMLInputElement|null} */ (e?.target);
      if (!selected) return;

      const variant = this.#readVariants().find((v) => v.id.toString() === selected.value);
      if (!variant) return;

      this.#applyVariantChange(variant, selected);
    }
  };

  /**
   * Apply variant change: update form, URL, labels, price, and dispatch event.
   * @param {Variant} variant
   * @param {HTMLInputElement} [selectedInput]
   */
  #applyVariantChange(variant, selectedInput) {
    const variantId = variant.id.toString();

    // Update hidden variant input
    if (this.refs.variantIdInput) {
      this.refs.variantIdInput.value = variantId;
    }

    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());

    // Update swatch value label
    if (selectedInput) {
      const swatchLabel = selectedInput.getAttribute('aria-label');
      if (swatchLabel) {
        const optionContainer = selectedInput.closest('[data-option-group]') ||
          selectedInput.closest('.flex.flex-col.gap-2');
        const valueSpan = optionContainer?.querySelector('[data-swatch-value]');
        if (valueSpan) valueSpan.textContent = swatchLabel;
      }
    }

    // Update price and availability
    this.#updatePriceDisplay(variant);

    // Dispatch custom event for other components (kit, gallery, etc.)
    this.dispatchEvent(new CustomEvent('aromely:variant-change', {
      bubbles: true,
      detail: { variantId: variant.id, price: variant.price, variant },
    }));
  }

  /**
   * When a selling plan option changes, update the hidden input + price.
   */
  #onSellingPlanChange = () => {
    const selected = /** @type {HTMLInputElement|null} */ (
      this.querySelector('input[name="selling-plan-option"]:checked')
    );
    if (!selected) return;

    const sellingPlanId = selected.value;

    // Update hidden selling plan input
    if (this.refs.sellingPlanInput) {
      this.refs.sellingPlanInput.value = sellingPlanId;
    }

    // Re-render price with the new selling plan context
    const currentVariantId = this.refs.variantIdInput?.value;
    const variant = this.#readVariants().find((v) => v.id.toString() === currentVariantId);
    if (variant) {
      this.#updatePriceDisplay(variant);
    }
  };

  /**
   * Repaint the price line for the current variant and selling plan in the
   * browser's promotion state.
   *
   * Every figure comes from the price map snippets/aromely-price.liquid wrote
   * on the line for both states (one-time and each plan, per variant); nothing
   * here computes a discount. The only arithmetic is the ATC total: the
   * per-unit price times the fit quantity.
   *
   * @param {Variant} variant
   * @param {boolean} [passive] Not user input: keep a loading/success button state.
   */
  #updatePriceDisplay(variant, passive = false) {
    const line = this.querySelector('[data-price-look="pdp"][data-price-map]');
    const map = readMap(line);
    const planId = this.refs.sellingPlanInput?.value || '';
    const entry = pickOption(map, variant.id, planId);
    // Keep the valid server price if a fragment has not supplied its map yet.
    if (!entry) return;
    if (entry) {
      paintReactive({ price: this.refs.priceDisplay, compare: this.refs.comparePriceDisplay, badge: this.refs.discountBadge }, entry);
      line.dataset.priceState = promoState();
    }

    // "then $X every delivery": only while the sale runs and a plan is selected.
    const then = /** @type {HTMLElement|null} */ (this.querySelector('[data-price-then]'));
    if (then) {
      const text = promoState() === 'on' && planId && entry ? thenText(map, entry) : '';
      then.textContent = text;
      then.hidden = !text;
    }

    const quantitySelect = this.querySelector('[data-fit-pdp-quantity] select:not(:disabled)');
    const quantity = quantitySelect ? Number(quantitySelect.value) : 1;
    const label = quantity > 1 ? formatMoney(entry.f * quantity, this.#moneyFormat, this.#currency) : entry.F;
    this.#updateButtonLabel(variant, label, !!planId, passive);
    // Gift eligibility uses the selected price map, including plan and fit quantity.
    document.dispatchEvent(new CustomEvent('aromely:size', {
      detail: { resource: { id: variant.id, price: variant.price, final_price: entry.f, quantity }, data: { productId: this.dataset.productId } },
    }));
  }

  /**
   * Update add-to-cart button label and availability.
   * @param {Variant} variant
   * @param {string} formattedPrice
   * @param {boolean} hasSellingPlan
   * @param {boolean} [passive]
   */
  #updateButtonLabel(variant, formattedPrice, hasSellingPlan, passive = false) {
    const btn = this.refs.submitBtn;
    if (!btn) return;

    // A repaint while adding to cart must not re-enable the button.
    const busy = passive && !!btn.dataset.state;

    if (variant.available) {
      if (!busy) {
        btn.disabled = false;
        btn.dataset.state = '';
      }
      if (this.refs.submitBtnLabel) {

        this.refs.submitBtnLabel.textContent = this.#tAddToCartFor.replace('__PRICE__', () => formattedPrice);
      }
    } else {
      btn.disabled = true;
      if (!busy) btn.dataset.state = '';
      if (this.refs.submitBtnLabel) {
        this.refs.submitBtnLabel.textContent = this.#tSoldOut;
      }
    }
  }

}

if (!customElements.get('aromely-pdp')) {
  customElements.define('aromely-pdp', AromelyPdp);
}
